import LeaderboardStats from './LeaderboardStats';
import React, { useEffect, useState } from 'react';
import { LeaderboardFrame, DateSelector, LeaderboardPanel, formatLeaderboardDate, fetchGameStatuses } from '@precisionpros/game-ui';

const API = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:8005/api/wordprofinder' : 'https://wordprofinder.precisionpros.com/api/wordprofinder');

const phoenixDate = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Phoenix', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const formatTime = (milliseconds) => `${(milliseconds / 1000).toFixed(2)}s`;
const hubHeaders = () => {
  const token = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken') || '';
  if (token) localStorage.setItem('hubToken', token);
  return token ? { 'X-Hub-Token': token } : {};
};

function SolutionOverlay({ puzzle, onClose }) {
  const selected = (row, col) => puzzle.path.some(([pathRow, pathCol]) => pathRow === row && pathCol === col);
  return <div className="solution-backdrop" role="dialog" aria-modal="true" aria-label="Puzzle solution" onClick={onClose}>
    <section className="solution-modal card" onClick={(event) => event.stopPropagation()}>
      <button className="solution-close" aria-label="Close solution" onClick={onClose}>×</button>
      <div className="eyebrow">SOLUTION</div>
      <h2 className="solution-word">{puzzle.word}</h2>
      <p className="muted">This word is highlighted below.</p>
      <div className="solution-grid">{puzzle.grid.map((row, rowIndex) => row.split('').map((letter, colIndex) => <div key={`${rowIndex}-${colIndex}`} className={`solution-cell ${selected(rowIndex, colIndex) ? 'solution-selected' : ''}`}>{letter}</div>))}</div>
      <button className="solution-done" onClick={onClose}>Close</button>
    </section>
  </div>;
}

export default function LeaderboardPage() {
  const playerName = new URLSearchParams(window.location.search).get('player_name') || localStorage.getItem('playerName') || '';
  const [daily, setDaily] = useState([]);
  const [allWords, setAllWords] = useState([]);
  const [showAllWords, setShowAllWords] = useState(false);
  const [leaderboardMode, setLeaderboardMode] = useState('straight');
  const [error, setError] = useState('');
  const [solutionPuzzle, setSolutionPuzzle] = useState(null);
  const [solutionLoading, setSolutionLoading] = useState(false);
  const [expandedPlayers, setExpandedPlayers] = useState(new Set());
  const [otherGamesStatus, setOtherGamesStatus] = useState(null);
  const [hasPlayedToday, setHasPlayedToday] = useState(null);
  const [selectedDate, setSelectedDate] = useState(phoenixDate);

  useEffect(() => {
    fetchGameStatuses()
      .then((statuses) => {
        setOtherGamesStatus(statuses);
        setHasPlayedToday(statuses.wordprofinder === true);
      })
      .catch(() => {
        setOtherGamesStatus({});
        setHasPlayedToday(false);
      });
  }, [playerName]);

  useEffect(() => {
    const playerQuery = playerName ? `&player_name=${encodeURIComponent(playerName)}` : '';
    fetch(`${API}/leaderboard/?date=${selectedDate}${playerQuery}`, { headers: hubHeaders() }).then(async (dailyResponse) => {
      const dailyData = await dailyResponse.json();
      if (!dailyResponse.ok) throw new Error('Unable to load leaderboard');
      setDaily(dailyData.results || []); setAllWords(dailyData.all_words || []); setShowAllWords(dailyData.show_all_words === true); setLeaderboardMode(dailyData.mode || 'straight'); if (selectedDate === phoenixDate()) setHasPlayedToday(dailyData.show_all_words === true);
    }).catch((err) => setError(err.message));
  }, [selectedDate]);

  const showSolution = async (word = '') => {
    setSolutionLoading(true);
    try {
      const wordQuery = word ? `&word=${encodeURIComponent(word)}` : '';
      const response = await fetch(`${API}/solution/?date=${selectedDate}${wordQuery}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load solution');
      setSolutionPuzzle(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSolutionLoading(false);
    }
  };

  const togglePlayerWords = (playerNameForRow) => setExpandedPlayers((current) => {
    const next = new Set(current);
    if (next.has(playerNameForRow)) next.delete(playerNameForRow); else next.add(playerNameForRow);
    return next;
  });

  const playerParam = playerName ? `?player_name=${encodeURIComponent(playerName)}` : '';
  const links = otherGamesStatus === null ? [] : [
    { key: 'wordpro', letter: 'W', color: '#4CAF50', href: `http://localhost:3000/${playerParam}`, played: !!otherGamesStatus.wordpro },
    { key: 'connectpro', letter: 'C', color: '#2563eb', href: `http://localhost:3001/${playerParam}`, played: !!otherGamesStatus.connectpro },
    { key: 'eventpro', letter: 'E', color: '#ea580c', href: `http://localhost:3002/${playerParam}`, played: !!otherGamesStatus.eventpro },
    { key: 'buildpro', letter: 'B', color: '#0f766e', href: `http://localhost:3003/${playerParam}`, played: !!otherGamesStatus.buildpro },
    { key: 'wordprofinder', letter: 'WF', color: '#4CAF50', href: `/leaderboard${playerParam}`, played: true },
    { key: 'seashroud', letter: 'S', color: '#287f89', href: '', played: !!otherGamesStatus.seashroud },
    { key: 'mathpro', letter: 'M', color: '#996128', href: '', played: !!otherGamesStatus.mathpro },
    { key: 'sevenapart', letter: '7', color: '#644a83', href: '', played: !!otherGamesStatus.sevenapart },
  ];
  const live = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  const gameHref = (base, played) => `${base}${played ? '/leaderboard' : ''}${playerParam}`;
  if (otherGamesStatus !== null && !live) {
    links[0].href = gameHref('http://localhost:3005', links[0].played);
    links[1].href = gameHref('http://localhost:3001', links[1].played);
    links[2].href = gameHref('http://localhost:3002', links[2].played);
    links[3].href = gameHref('http://localhost:3003', links[3].played);
    links[5].href = gameHref('http://localhost:3006', links[5].played);
    links[6].href = gameHref(`http://${window.location.hostname}:3008`, links[6].played);
    links[7].href = gameHref(`http://${window.location.hostname}:3007`, links[7].played);
  }
  if (otherGamesStatus !== null && live) {
    links[0].href = gameHref('https://wordpro.precisionpros.com', links[0].played);
    links[1].href = gameHref('https://connectpro.precisionpros.com', links[1].played);
    links[2].href = gameHref('https://eventpro.precisionpros.com', links[2].played);
    links[3].href = gameHref('https://buildpro.precisionpros.com', links[3].played);
    links[5].href = gameHref('https://seashroud.precisionpros.com', links[5].played);
    links[6].href = gameHref('https://mathpro.precisionpros.com', links[6].played);
    links[7].href = gameHref('https://sevenapart.precisionpros.com', links[7].played);
  }
  const game = { name: 'Word', wordmarkMain: 'Word', wordmarkAccent: 'Pro', wordmarkSuffix: 'Finder', badge: 'WF', badgeColor: '#000', accent: '#4CAF50' };
  const shouldHideToday = selectedDate === phoenixDate() && hasPlayedToday !== true;
  return <LeaderboardFrame game={game} currentGameKey="wordprofinder" title="Leaderboard" links={links} homeHref={live ? 'https://games.precisionpros.com' : 'http://localhost:3000'} showPrePlayBanner={shouldHideToday} playHref={`${window.location.origin}/`} howToPlayHref={`${window.location.origin}/how-to-play`} dateSelector={<DateSelector dateLabel={formatLeaderboardDate(selectedDate)} onPrevious={() => setSelectedDate((value) => { const dateValue = new Date(`${value}T12:00:00`); dateValue.setDate(dateValue.getDate() - 1); return dateValue.toISOString().slice(0, 10); })} onNext={() => setSelectedDate((value) => { const dateValue = new Date(`${value}T12:00:00`); dateValue.setDate(dateValue.getDate() + 1); return dateValue.toISOString().slice(0, 10); })} />}>
    {error && <section className="card"><p>{error}</p></section>}
    {otherGamesStatus !== null && !shouldHideToday && <LeaderboardPanel game={game} subtitle={leaderboardMode === 'flexible' || leaderboardMode === 'multi_straight' ? 'Ranked by words found, then time.' : 'Completed solves, ranked by time.'}><div className="leaderboard-heading modern-section-heading"><h2>Today’s Results</h2></div>{showAllWords && allWords.length > 0 && <div className="possible-words"><strong>All possible words:</strong> {allWords.map((word) => <button key={word} className="word-link" onClick={() => showSolution(word)}>{word}</button>)}</div>}{daily.length === 0 ? <p className="muted">No attempts yet.</p> : <table><thead><tr><th>Rank</th><th>Name</th><th>Words found</th><th>Time</th></tr></thead><tbody>{daily.map((row, index) => { const foundWords = row.found_words || []; const expanded = expandedPlayers.has(row.player_name); return <React.Fragment key={`${row.player_name}-${index}`}><tr className="leaderboard-row"><td>{row.completed ? (() => { const rankIndex = daily.findIndex(other => other.completed && other.solve_time_ms === row.solve_time_ms && (other.found_words || []).length === (row.found_words || []).length); return ['🥇','🥈','🥉'][rankIndex] || rankIndex + 1; })() : '—'}</td><td>{row.player_name}</td><td><button className="word-count-button" onClick={() => togglePlayerWords(row.player_name)} aria-expanded={expanded}>{foundWords.length}<span>{expanded ? ' ▲' : ' ▼'}</span></button></td><td>{row.solve_time_ms != null ? formatTime(row.solve_time_ms) : '-'}</td></tr>{expanded && <tr className="word-details-row"><td colSpan="4">{foundWords.length ? foundWords.join(', ') : 'No words found'}</td></tr>}</React.Fragment>; })}</tbody></table>}</LeaderboardPanel>}
    <LeaderboardStats endpoint={`${API}/leaderboard-records/`} date={selectedDate} />
    {solutionPuzzle && <SolutionOverlay puzzle={solutionPuzzle} onClose={() => setSolutionPuzzle(null)} />}</LeaderboardFrame>;
}
